/**
 * How a part's market price has moved, and what that says about restocking it.
 *
 * BrickLink's price guide is a rolling six-month window, so every sighting of it is a picture of the market at
 * that moment rather than a price on one day. A series of those pictures shows the trend: a part whose average
 * keeps climbing is one worth buying more of, and a part whose average keeps falling is not.
 *
 * Pure and browser-safe: the API, the worker and the web app all use this one definition.
 */

import type { PriceGuideRegion, PriceGuideType } from './sell-through';

/** One sighting of BrickLink's price guide for a part, in a region, for one guide. */
export interface MarketObservation {
  /** When the guide was fetched. */
  at: Date | string;
  currency: string | null;
  minPrice: string | null;
  avgPrice: string | null;
  qtyAvgPrice: string | null;
  maxPrice: string | null;
  /** Pieces sold (sold guide) or listed (stock guide). */
  quantity: number | null;
  /** Lots, not pieces. */
  lots: number | null;
}

/** One point on the chart. */
export interface TrendPoint {
  at: string;
  currency: string | null;
  min: number | null;
  avg: number | null;
  qtyAvg: number | null;
  max: number | null;
  quantity: number | null;
  lots: number | null;
}

/**
 * What the series says about the part.
 *
 *   rising       the market has gone up over the period: restocking looks worth it
 *   falling      the market has gone down: restocking looks a poor idea
 *   flat         the market has held steady
 *   too_little   there is not enough history to say anything, which is itself worth saying plainly
 */
export type TrendState = 'rising' | 'falling' | 'flat' | 'too_little';

/** How a trend is judged. Kept here so the same thresholds are used everywhere. */
export const TREND_MIN_POINTS = 3;
/** A move of less than this is noise, not a trend. 2% of the average. */
export const TREND_FLAT_PCT = 2;

export interface PriceTrend {
  state: TrendState;
  /** The newest average, and the oldest one we can compare it with. */
  latestAvg: number | null;
  earliestAvg: number | null;
  /** The change from the oldest to the newest, in the currency of the figures. Null when there is no change to say. */
  change: number | null;
  /** The same change as a percentage of the earliest average. Null when it cannot be worked out. */
  changePct: number | null;
  /** How many sightings the judgement is based on. */
  points: number;
  /** The currency of the figures, when they agreed on one. */
  currency: string | null;
  /** The first and last dates of the series, so a chart can show the span it covers. */
  from: string | null;
  to: string | null;
  /** A plain-English reading, for people. */
  summary: string;
}

const num = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const iso = (v: Date | string): string => (v instanceof Date ? v.toISOString() : new Date(v).toISOString());

/** One point for a chart, from one sighting. */
export function toTrendPoint(o: MarketObservation): TrendPoint {
  return {
    at: iso(o.at),
    currency: o.currency,
    min: num(o.minPrice),
    avg: num(o.avgPrice),
    qtyAvg: num(o.qtyAvgPrice),
    max: num(o.maxPrice),
    quantity: o.quantity,
    lots: o.lots,
  };
}

/**
 * The series for a chart, oldest first. Sightings with the same time are kept apart; the chart plots them
 * on top of each other, which is the honest thing to do when two figures were fetched at the same moment.
 */
export function toTrendSeries(observations: MarketObservation[]): TrendPoint[] {
  return observations
    .map(toTrendPoint)
    .sort((a, b) => a.at.localeCompare(b.at));
}

/**
 * Reads the trend off a series of averages.
 *
 * The first and last averages that exist are compared. That is deliberately simple: BrickLink's own window
 * moves, so a series of averages is a series of overlapping windows, and the difference between the first and
 * the last is the honest measure of how much the market has moved. When the oldest average is missing, the
 * oldest one that exists is used instead, so a part whose history starts later is not judged on a fiction.
 */
export function priceTrend(observations: MarketObservation[]): PriceTrend {
  const points = toTrendSeries(observations);
  const withAvg = points.filter((p) => p.avg !== null);
  const currency = points.find((p) => p.currency)?.currency ?? null;

  if (withAvg.length < TREND_MIN_POINTS) {
    return {
      state: 'too_little',
      latestAvg: withAvg.length ? withAvg[withAvg.length - 1]!.avg : null,
      earliestAvg: withAvg.length ? withAvg[0]!.avg : null,
      change: null,
      changePct: null,
      points: withAvg.length,
      currency,
      from: points.length ? points[0]!.at : null,
      to: points.length ? points[points.length - 1]!.at : null,
      summary:
        withAvg.length === 0
          ? 'No average price has been seen for this part yet.'
          : `Only ${withAvg.length === 1 ? 'one price has' : `${withAvg.length} prices have`} been seen so far, which is not enough to show a trend.`,
    };
  }

  const earliest = withAvg[0]!.avg!;
  const latest = withAvg[withAvg.length - 1]!.avg!;
  const change = latest - earliest;
  // Rounded before it is judged: 1.00 to 1.02 is 2%, but the division gives 2.0000000000000018%, which would
  // read as a rise. Six places is well past any real price change and well short of that noise.
  const changePct = earliest === 0 ? null : Math.round(((change / earliest) * 100) * 1e6) / 1e6;

  let state: TrendState;
  if (changePct === null) state = 'flat';
  else if (changePct > TREND_FLAT_PCT) state = 'rising';
  else if (changePct < -TREND_FLAT_PCT) state = 'falling';
  else state = 'flat';

  const direction = state === 'rising' ? 'risen' : state === 'falling' ? 'fallen' : 'held steady';
  const pct = changePct === null ? '' : ` (${changePct > 0 ? '+' : ''}${Math.round(changePct * 10) / 10}%)`;

  return {
    state,
    latestAvg: latest,
    earliestAvg: earliest,
    change,
    changePct,
    points: withAvg.length,
    currency,
    from: points[0]!.at,
    to: points[points.length - 1]!.at,
    summary: `The market average has ${direction}${pct} over the ${withAvg.length} sightings held.`,
  };
}

/**
 * What the trend means for buying more of the part. This is the question the chart is for, so it is said here
 * in one place rather than left to each screen.
 */
export function restockVerdict(trend: PriceTrend): { tone: 'good' | 'neutral' | 'bad'; text: string } {
  switch (trend.state) {
    case 'rising':
      return { tone: 'good', text: 'The market is going up, so buying more of this part looks worth it.' };
    case 'falling':
      return { tone: 'bad', text: 'The market is going down, so buying more of this part is likely a poor idea.' };
    case 'flat':
      return { tone: 'neutral', text: 'The market has held steady, so the price now is about the usual one.' };
    case 'too_little':
      return { tone: 'neutral', text: 'There is not enough history yet to say whether the price is moving.' };
  }
}
